import { Success, Warning } from '@nosinovacao/fe-icons';
import { Button, cn, Sheet, useInMediaQuery } from '@nosinovacao/nosid-mfe-common';
import { type ComponentProps } from 'react';

export function SheetAlertLight({
  className,
  children,
  onOpenChange,
  overlayClassName,
  ...props
}: Readonly<Omit<ComponentProps<typeof Sheet>, 'skin' | 'animationDirection'>>) {
  const isTablet = useInMediaQuery('tablet');
  return (
    <Sheet
      skin="light"
      onOpenChange={onOpenChange}
      {...props}
      canClose={isTablet}
      animationDirection={isTablet ? 'right' : 'bottom'}
      overlayClassName={cn(overlayClassName, 'z-[60]')}
      className={cn(
        'z-[60] max-tablet:top-[unset] max-tablet:rounded-t-lg max-tablet:pb-4 max-tablet:pt-12',
        className,
      )}
    >
      {isTablet ? null : (
        <button
          data-tid="button-sheet-handle"
          className="absolute left-1/2 top-4 h-[6px] w-6 -translate-x-1/2 rounded-full bg-nos-neutral-200"
          onClick={() => {
            onOpenChange?.(false);
          }}
        ></button>
      )}
      {children}
    </Sheet>
  );
}

const iconForsheetAlertType = {
  destructive: Warning,
  default: Success,
} as const;

export function SheetAlertContentLight({
  description,
  title,
  type,
  cancel,
  confirm,
}: Readonly<{
  title: string;
  description: string;
  type: 'destructive' | 'default';
  cancel?: Omit<ComponentProps<typeof Button>, 'skin' | 'variant' | 'destructive'>;
  confirm?: Omit<ComponentProps<typeof Button>, 'skin' | 'variant' | 'destructive'>;
}>) {
  const Icon = iconForsheetAlertType[type];
  return (
    <div className="grid gap-8 tablet:h-full tablet:grid-rows-[1fr_max-content]">
      <div className="grid gap-2 break-words tablet:mt-9 tablet:h-max tablet:self-center">
        <Icon
          width={48}
          height={48}
          className={cn(
            type === 'destructive' && 'text-nos-red',
            type === 'default' && 'text-nos-neutral-1000',
          )}
        />
        <span data-tid="title-alert" className="font-bold text-nos-neutral-1000 font-title-h3">
          {title}
        </span>
        <span data-tid="text-alert-description" className="text-nos-neutral-700 font-text-p1">
          {description}
        </span>
      </div>
      <div className="grid gap-4 tablet:grid-cols-[max-content_max-content] tablet:gap-2">
        {cancel ? (
          <Button
            data-tid="button-alert-cancel"
            skin="light"
            {...cancel}
            className={cn(cancel.className, 'tablet:order-1')}
            variant="secondary"
          />
        ) : null}
        {confirm ? (
          <Button
            data-tid="button-alert-confirm"
            skin="light"
            {...confirm}
            className={cn(confirm.className, 'tablet:min-w-44')}
            destructive
          />
        ) : null}
      </div>
    </div>
  );
}
